// /resources/js/utils/formatters.js

/**
 * @module formatters
 * A collection of stateless, pure functions for formatting data into
 * user-friendly, presentational strings (often including HTML).
 * These can be used anywhere in the application.
 */

/**
 * Formats a number into a compact, human-readable string (e.g., 1.2M, 5.1K).
 * @param {number|string|null|undefined} val - The numeric value to format.
 * @param {boolean} [isCurrency=true] - Whether to prefix with a dollar sign.
 * @returns {string} The formatted string, or '...' if the input is invalid.
 */
export function formatNumber(val, isCurrency = true) {
    if (val === null || val === undefined) return '...';
    const num = parseFloat(val);
    if (isNaN(num)) return '...';
    const prefix = isCurrency ? '$' : '';
    if (Math.abs(num) >= 1_000_000_000) return `${prefix}${(num / 1_000_000_000).toFixed(2)}B`;
    if (Math.abs(num) >= 1_000_000) return `${prefix}${(num / 1_000_000).toFixed(2)}M`;
    if (Math.abs(num) >= 1_000) return `${prefix}${(num / 1_000).toFixed(1)}K`;
    return `${prefix}${num.toFixed(0)}`;
}

/**
 * Formats a number as a percentage string with appropriate color.
 * @param {number|string|null|undefined} val - The numeric value (e.g., 10.5 for 10.5%).
 * @returns {string} The formatted HTML string, or '...' if the input is invalid.
 */
export function formatPct(val, showPlusSign = false) {
    if (val === null || val === undefined) return '...';
    const num = parseFloat(val);
    if (isNaN(num)) return '...';
    const color = num >= 0 ? 'text-success' : 'text-danger';
    // --- START: THE FIX ---
    // The sign is now conditional based on the new `showPlusSign` parameter.
    const sign = num >= 0 && showPlusSign ? '+' : '';
    // --- END: THE FIX ---
    const precision = Math.abs(num) > 1000 ? 0 : 2;
    return `<span class="${color}">${sign}${num.toFixed(precision)}%</span>`;
}
/**
 * Formats a number as an hours string (e.g., "4.5h").
 * @param {number|string|null|undefined} val - The numeric value in hours.
 * @returns {string} The formatted HTML string, or a placeholder if invalid.
 */
export function formatHours(val) {
    if (val === null || val === undefined) return '<span class="text-text-dark">N/A</span>';
    return `${parseFloat(val).toFixed(1)}h`;
}

/**
 * Formats a factor/multiplier with appropriate color coding.
 * @param {number|string|null|undefined} val - The numeric factor value.
 * @returns {string} The formatted HTML string, or a placeholder if invalid.
 */
export function formatFactor(val) {
    if (val === null || val === undefined) return '<span class="text-text-dark">N/A</span>';
    const num = parseFloat(val);
    if (isNaN(num)) return '<span class="text-text-dark">N/A</span>';
    const color = num >= 1.5 ? 'text-success' : (num >= 1.0 ? 'text-yellow-400' : 'text-danger');
    return `<span class="${color}">${num.toFixed(2)}</span>`;
}